import {
  type AcceptRoleCore,
  type Attachment,
  type DeliveryNoteAdminEnhanced,
  type DeliveryNoteAdminLineItem,
  type DeliveryNoteAdminWithRelatedData,
} from '../types';

import { getCustomData } from './getCustomData';
import { getLineItemAcceptState } from './getLineItemAcceptState';
import { getLineItemBillingState } from './getLineItemBillingState';

/**
 * Maps the raw line item data from the API and combines it with the already
 * preprocessed delivery note data to a format that's directly usable in the frontend.
 * @param lineItems - The line items to map.
 * @param mappedDeliveryNote - The mapped delivery note.
 * @param attachments - The attachments of the delivery note.
 * @param invoices - The invoices referenced in the delivery note.
 * @returns The mapped line items data.
 */
export const mapLineItemsData = (
  lineItems: DeliveryNoteAdminLineItem[],
  mappedDeliveryNote: DeliveryNoteAdminEnhanced,
  attachments: Attachment[] = [],
  invoices: DeliveryNoteAdminWithRelatedData['bulkInvoices'] = [],
) => {
  if (!lineItems?.length || !mappedDeliveryNote) {
    return [];
  }

  // List of roles we are interested to know the accept state of
  const relevantAcceptingRoles: readonly AcceptRoleCore[] = [
    'recipient',
    'carrier',
    'supplier',
  ] as const;

  const mappedLineItemsData =
    lineItems.map((lineItem, lineItemIndex) => {
      return {
        ...lineItem,
        acceptState: Object.fromEntries(
          relevantAcceptingRoles.map((role) => [
            role,
            getLineItemAcceptState(
              lineItemIndex,
              mappedDeliveryNote?.acceptedItems,
              attachments,
              role,
            ),
          ]),
        ),
        billingState: getLineItemBillingState(
          lineItemIndex,
          mappedDeliveryNote?.billedItems,
          invoices,
        ),
        customData: getCustomData(lineItem?.additionalPartyData),
        id: lineItem?.id?.trim() ?? lineItemIndex,
        lineItemIndex,
      };
    }) ?? [];

  return mappedLineItemsData;
};
